/**
 * Sunucunun hata alanı kullanıcıya kısa, anlamlı bir cümle olarak gösterilir
 * (owner 2026-10-15: "duplicate_code gibi saçma şeyler değil, kısa öz anlamlı
 * kelimeler").
 *
 * - Bilinen makine kodu (`duplicate_code`, `category_exists` …) çevrilir;
 *   `value` varsa cümleye girer ("123 kodu başka bir üründe kayıtlı.").
 * - Bilinmeyen kod ya da teknik İngilizce metin ("Failed to …", "HTTP 500")
 *   yerine çağıranın verdiği yedek cümle, o da yoksa genel cümle gelir.
 * - Zaten okunur bir cümle (boşluk içeren, teknik kalıba uymayan) aynen kalır.
 */
import i18n from '../i18n';

const K = 'api_errors.';
/** `duplicate_code`, `invalid_id`; en az bir alt çizgi, boşluk yok. */
const CODE_RE = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/;
/** Tek kelimelik makine kodları (`ambiguous`, `forbidden`). */
const WORD_CODES = new Set(['ambiguous', 'forbidden', 'unauthorized', 'error']);
/** Sunucunun ya da tarayıcının teknik İngilizce metinleri. */
const TECH_RE =
  /^(failed to\b|http \d|unknown error|request failed|network ?error|internal server error|load failed|unexpected token|typeerror|syntaxerror|[a-z_ ]+ must be\b|[a-z_]+ is required$)/i;

const has = (key: string) => i18n.exists(K + key);

interface Options {
  /** Sunucunun `value` alanı (ör. çakışan kod). */
  value?: unknown;
  /** Kod tanınmazsa gösterilecek, ekrana özgü cümle. */
  fallback?: string;
}

const fallbackText = (fallback?: string) => fallback || i18n.t(`${K}generic`);

export function errorText(raw: unknown, options: Options = {}): string {
  const { value, fallback } = options;
  const text = (
    raw instanceof Error ? raw.message : typeof raw === 'string' ? raw : ''
  ).trim();
  if (!text) return fallbackText(fallback);

  // "duplicate_code: 123" biçimi: kod ve değer.
  const m = text.match(/^([a-z][a-z0-9_]*):\s*(.+)$/);
  const code = (m ? m[1] : text).toLowerCase();
  const val = value ?? (m ? m[2] : undefined);

  if (CODE_RE.test(code) || WORD_CODES.has(code)) {
    if (val !== undefined && val !== '' && has(`${code}_value`)) {
      return i18n.t(`${K}${code}_value`, { value: String(val) });
    }
    if (has(code)) return i18n.t(K + code);
    if (code.startsWith('invalid_') || code.startsWith('bad_'))
      return i18n.t(`${K}invalid_input`);
    if (code.endsWith('_not_found')) return i18n.t(`${K}not_found`);
    if (code.endsWith('_required') || code.endsWith('_empty'))
      return i18n.t(`${K}missing_input`);
    return fallbackText(fallback);
  }
  if (/^access denied$/i.test(text) || /^forbidden$/i.test(text))
    return i18n.t(`${K}permission_denied`);
  if (/ not found$/i.test(text) && /^[\x20-\x7e]+$/.test(text))
    return i18n.t(`${K}not_found`);
  if (TECH_RE.test(text)) return fallbackText(fallback);
  return text;
}
